import { SupportedLanguage } from '@/constants/supportedLanguages';
import { cn } from '@/lib/utils';
import { SUPPORTED_LANGUAGES } from '@/constants/supportedLanguages';

interface LanguageToggleProps {
  className?: string;
  onChange: (language: SupportedLanguage) => void;
  value: SupportedLanguage;
  localLanguage?: SupportedLanguage;
}

export function LanguageToggle({ 
  className,
  onChange,
  value,
  localLanguage = 'hindi',
}: LanguageToggleProps) {
  const handleToggle = () => {
    // Toggle between English and the local language
    const newLanguage = value === 'english' ? localLanguage : 'english';
    onChange(newLanguage);
  };

  // Ensure localLanguage is valid, fallback to hindi if not
  const validLocalLanguage = SUPPORTED_LANGUAGES.find(l => l.value === localLanguage) ? localLanguage : 'hindi';

  return (
    <div 
      className={cn(
        "relative flex items-center justify-between bg-blue-500 rounded-full p-1 w-20 h-9 cursor-pointer shadow-sm hover:shadow-md transition-shadow duration-200",
        className
      )}
      onClick={handleToggle}
      role="button"
      tabIndex={0}
      aria-label={`Toggle language to ${value === 'english' ? validLocalLanguage : 'english'}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleToggle();
        }
      }}
    >
      <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-full flex items-center">
        {/* Toggle slider - changed to elliptical shape */}
        <div 
          className={cn(
            "absolute w-9 h-7 bg-white rounded-full transition-transform duration-300 shadow-sm",
            value === 'english' ? 'translate-x-0.5' : 'translate-x-10'
          )}
        />
      </div>

      {/* English Option */}
      <div 
        className={cn(
          "z-10 flex items-center justify-center w-10 h-7 rounded-full transition-colors duration-300 text-sm font-bold",
          value === 'english' ? "text-blue-800" : "text-white"
        )}
      >
        A
      </div>

      {/* Local Language Option */}
      <div 
        className={cn(
          "z-10 flex items-center justify-center w-10 h-7 rounded-full transition-colors duration-300 text-lg font-bold",
          value !== 'english' ? "text-blue-800" : "text-white"
        )}
      >
        {(() => {
          const langObj = SUPPORTED_LANGUAGES.find(l => l.value === validLocalLanguage);
          if (langObj) {
            if (langObj.toggleChar) return langObj.toggleChar;
            if (langObj.nativeLabel) return langObj.nativeLabel.charAt(0).toUpperCase();
          }
          return validLocalLanguage.charAt(0).toUpperCase();
        })()}
      </div>
    </div>
  );
}

export default LanguageToggle; 